import React, { useMemo } from 'react';
import TeamJersey from './TeamJersey';
import QuestionCard from './QuestionCard';
import { QuestionResultIndicator } from './shared/QuestionResultIndicator';
import { createJerseyMap, type Team } from '../utils/jersey-resolver';

interface TeamPickProps {
  questionId?: string;
  questionText: string;
  teams: [Team, Team];
  selectedTeam?: string;
  onTeamSelect: (teamId: string | undefined) => void;
  points: number;
  earnedPoints?: number;
  isCorrect?: boolean;
  disabled?: boolean;
  className?: string;
  questionNumber?: number;
}

const TeamPick: React.FC<TeamPickProps> = ({
  questionId,
  questionText,
  teams,
  selectedTeam,
  onTeamSelect,
  points,
  earnedPoints,
  isCorrect,
  disabled = false,
  className = '',
  questionNumber,
}) => {
  // Debug logging
  console.log('[TeamPick] Render:', {
    questionId,
    selectedTeam,
    selectedTeamType: typeof selectedTeam,
    isUndefined: selectedTeam === undefined,
    teams: teams.map(t => ({ id: t.id, isSelected: selectedTeam === t.id }))
  });

  // Memoize team jersey data to prevent random changes and unnecessary re-renders
  const teamJerseyMap = useMemo(() => createJerseyMap(teams), [teams]);

  return (
    <QuestionCard
      points={points}
      earnedPoints={earnedPoints}
      className={className}
      questionNumber={questionNumber}
      data-test-id={questionId ? `question-${questionId}` : undefined}
      data-test-type="team"
    >
      {/* Question Text */}
      <span data-test-id="team-question" className="text-text-primary font-medium block text-center mb-4">{questionText}</span>

      {/* Team Selection */}
      <div className="grid grid-cols-2 gap-4" data-test-id="team-selection">
        {teams.map((team, index) => {
          const isSelected = selectedTeam === team.id;
          const hasResult = isCorrect !== undefined;
          const resultBorderClass =
            isSelected && hasResult
              ? isCorrect
                ? 'border-green-500'
                : 'border-red-500'
              : '';

          const resultBgClass = isSelected && hasResult ? 'bg-white' : '';
          const isLeftOption = index === 0;

          return (
            <button
              key={team.id}
              onClick={() => {
                console.log('[TeamPick] Button clicked:', {
                  questionId,
                  teamId: team.id,
                  isSelected,
                  willPassValue: isSelected ? undefined : team.id,
                  disabled
                });
                if (!disabled) onTeamSelect(isSelected ? undefined : team.id);
              }}
              disabled={disabled}
              data-test-id={`option-${team.id}`}
              data-test-result={isSelected && hasResult ? (isCorrect ? 'correct' : 'incorrect') : undefined}
              data-selected={isSelected ? 'true' : 'false'}
              className={`
                group peer w-full h-auto min-h-[140px] p-6 rounded-xl transition-smooth flex flex-col items-center justify-center relative
                ${
                  isSelected
                    ? `bg-white shadow-lg border-2 border-white ${
                        !hasResult ? 'peer-hover:bg-[#555555] peer-hover:border-0 peer-hover:shadow-none' : ''
                      }`
                    : `bg-[#555555] border-0 ${
                        !hasResult ? 'hover:bg-white hover:border-2 hover:border-white hover:shadow-lg' : ''
                      }`
                }
                ${resultBorderClass}
                ${disabled && !hasResult ? 'cursor-not-allowed opacity-90' : ''}
              `}
            >
              {/* Result Icon */}
              {isSelected && hasResult && (
                <QuestionResultIndicator
                  isCorrect={isCorrect}
                  position={isLeftOption ? 'left' : 'right'}
                />
              )}

              <div className="flex flex-col items-center space-y-3">
                {/* Team Jersey Circle */}
                <TeamJersey size="md" jerseyUrl={teamJerseyMap.get(team.id)} />

                {/* Team Name */}
                <div className={`${team.name.split(' ').some(l => l.length > 14) ? 'text-md' : 'text-lg'
                  } min-h-12 ${
                  isSelected
                    ? `text-gray-900 ${!hasResult ? 'peer-hover:text-text-primary' : ''}`
                    : `text-text-primary ${!hasResult ? 'group-hover:text-gray-900' : ''}`
                }`}>
                  {team.name}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </QuestionCard>
  );
};

export default TeamPick;
